import { forwardRef, useEffect, useState } from 'react';

import { ProductType } from '@xing-com/crate-common-graphql-types';
import { IconCircleTickFilled } from '@xing-com/icons';
import { List } from '@xing-com/list';
import { scale110 } from '@xing-com/tokens';
import { BodyCopy, Headline, Hero } from '@xing-com/typography';

import { PerkListSkeleton } from './perk-list-skeleton';
import * as Styled from './perk-list.styled';

export type PerkGroup = {
  heading: string;
  perks: readonly string[];
};

export type ProductPerks = {
  activationCta: string;
  intro: string;
  title: string;
  groups: readonly PerkGroup[];
};

export type PerkListProps = {
  activationUrls?: Partial<Record<ProductType, string>>;
  initialProduct: ProductType;
  isLoading?: boolean;
  heading?: string;
  premiumPerks?: ProductPerks;
  proJobsPerks?: ProductPerks;
};

export const PerkList = forwardRef<HTMLElement, PerkListProps>(
  function PerkList(
    {
      activationUrls,
      initialProduct,
      isLoading = false,
      heading,
      premiumPerks,
      proJobsPerks,
    },
    ref
  ) {
    const [activeProduct, setActiveProduct] = useState(initialProduct);

    useEffect(() => {
      setActiveProduct(initialProduct);
    }, [initialProduct]);

    if (isLoading) {
      return <PerkListSkeleton />;
    }

    if (!heading || !premiumPerks || !proJobsPerks) {
      return null;
    }

    const products = {
      [ProductType.Premium]: {
        perks: premiumPerks,
      },
      [ProductType.Projobs]: {
        perks: proJobsPerks,
      },
    };
    const activeProductDetails = products[activeProduct];

    return (
      <Styled.Section aria-labelledby="perk-list-heading" ref={ref}>
        <Styled.Overview>
          <Headline as="h2" id="perk-list-heading" noMargin size="xxlarge">
            {heading}
          </Headline>
          <Styled.ContentSwitcher
            activeItemIndex={activeProduct === ProductType.Premium ? 0 : 1}
            allowDropdown={false}
            data={[
              {
                copy: products[ProductType.Premium].perks.title,
                onClick: () => setActiveProduct(ProductType.Premium),
              },
              {
                copy: products[ProductType.Projobs].perks.title,
                onClick: () => setActiveProduct(ProductType.Projobs),
              },
            ]}
            size="medium"
          />
        </Styled.Overview>

        <Styled.ProductHeader>
          <Styled.ProductTitle>
            <Hero as="h3" size="medium" noMargin>
              {activeProductDetails.perks.title}
            </Hero>
          </Styled.ProductTitle>
          <Styled.Intro>
            <BodyCopy noMargin size="large">
              {activeProductDetails.perks.intro}
            </BodyCopy>
          </Styled.Intro>
          <Styled.ActivationButton
            href={activationUrls?.[activeProduct]}
            size="large"
            variant="primary"
          >
            {activeProductDetails.perks.activationCta}
          </Styled.ActivationButton>
        </Styled.ProductHeader>

        <Styled.Groups data-qa="perk-list">
          {activeProductDetails.perks.groups.map((group) => (
            <Styled.Group key={group.heading}>
              <Headline as="h4" noMargin size="xlarge">
                {group.heading}
              </Headline>
              <List>
                {group.perks.map((perk) => (
                  <Styled.Perk key={perk}>
                    <IconCircleTickFilled
                      aria-hidden="true"
                      height={scale110}
                      width={scale110}
                    />
                    <BodyCopy as="span" noMargin size="medium">
                      {perk}
                    </BodyCopy>
                  </Styled.Perk>
                ))}
              </List>
            </Styled.Group>
          ))}
        </Styled.Groups>
      </Styled.Section>
    );
  }
);
